import React, { useState, useEffect } from 'react';
import { Save, User, MapPin, Calendar, CreditCard, Phone, Mail, Briefcase, Building } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import axios from 'axios';

// URL centralizado que funciona em produção mesmo com .env errado
import { getBackendUrl } from '../utils/config';
const API_URL = getBackendUrl();

const MeuPerfilTab = () => {
  const [perfil, setPerfil] = useState(null);
  const [loading, setLoading] = useState(true);
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    carregarPerfil();
  }, []);

  const carregarPerfil = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/menu-colaborador/perfil/meu`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('Perfil carregado:', response.data);
      setPerfil(response.data);
    } catch (error) {
      console.error('Erro ao carregar perfil:', error);
      toast.error('Erro ao carregar perfil');
    } finally {
      setLoading(false);
    }
  };

  const handleSalvar = async () => {
    try {
      setSalvando(true);
      const token = localStorage.getItem('token');
      
      // Apenas enviar campos editáveis (colaborador NÃO pode editar seccao, funcao, data_entrada)
      const updateData = {
        nome_completo: perfil.nome_completo,
        morada: perfil.morada,
        data_nascimento: perfil.data_nascimento,
        cartao_cidadao: perfil.cartao_cidadao,
        nif: perfil.nif,
        niss: perfil.niss,
        telemovel: perfil.telemovel,
        email: perfil.email
      };
      
      const response = await axios.put(
        `${API_URL}/api/menu-colaborador/perfil/meu`,
        updateData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setPerfil(response.data);
      toast.success('Perfil atualizado com sucesso!');
    } catch (error) {
      console.error('Erro ao salvar perfil:', error);
      toast.error(error.response?.data?.detail || 'Erro ao salvar perfil');
    } finally {
      setSalvando(false);
    }
  };

  const handleChange = (campo, valor) => {
    setPerfil(prev => ({
      ...prev,
      [campo]: valor
    }));
  };

  if (loading || !perfil) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
        <div className="text-gray-600">A carregar perfil...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-lg font-semibold">Meu Perfil</h2>
          <p className="text-sm text-gray-600 mt-1">
            Preencha os seus dados pessoais. Percentagem de completude: <span className="font-semibold text-blue-600">{perfil?.percentagem_completo || 0}%</span>
          </p>
        </div>
        <Button onClick={handleSalvar} disabled={salvando}>
          <Save className="h-4 w-4 mr-2" />
          {salvando ? 'A guardar...' : 'Guardar'}
        </Button>
      </div>

      {/* Barra de Progresso */}
      <Card>
        <CardContent className="pt-6">
          <div className="w-full bg-gray-200 rounded-full h-3">
            <div
              className="bg-blue-600 h-3 rounded-full transition-all duration-500"
              style={{ width: `${perfil?.percentagem_completo || 0}%` }}
            />
          </div>
        </CardContent>
      </Card>

      {/* Dados Pessoais */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <User className="h-5 w-5 mr-2" />
            Dados Pessoais
          </CardTitle>
          <CardDescription>Informação pessoal e de contacto</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="nome_completo">Nome Completo *</Label>
              <Input
                id="nome_completo"
                value={perfil?.nome_completo || ''}
                onChange={(e) => handleChange('nome_completo', e.target.value)}
                placeholder="Nome completo"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="data_nascimento">Data de Nascimento *</Label>
              <Input
                id="data_nascimento"
                type="date"
                value={perfil?.data_nascimento || ''}
                onChange={(e) => handleChange('data_nascimento', e.target.value)}
              />
            </div>

            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="morada">Morada *</Label>
              <Input
                id="morada"
                value={perfil?.morada || ''}
                onChange={(e) => handleChange('morada', e.target.value)}
                placeholder="Rua, número, código postal, localidade"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="cartao_cidadao">Cartão de Cidadão *</Label>
              <Input
                id="cartao_cidadao"
                value={perfil?.cartao_cidadao || ''}
                onChange={(e) => handleChange('cartao_cidadao', e.target.value)}
                placeholder="000000000ZZ0"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="nif">NIF *</Label>
              <Input
                id="nif"
                value={perfil?.nif || ''}
                onChange={(e) => handleChange('nif', e.target.value)}
                placeholder="123456789"
                maxLength={9}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="niss">NISS *</Label>
              <Input
                id="niss"
                value={perfil?.niss || ''}
                onChange={(e) => handleChange('niss', e.target.value)}
                placeholder="12345678901"
                maxLength={11}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="telemovel">Telemóvel *</Label>
              <Input
                id="telemovel"
                value={perfil?.telemovel || ''}
                onChange={(e) => handleChange('telemovel', e.target.value)}
                placeholder="+351 912345678"
              />
            </div>

            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                value={perfil?.email || ''}
                onChange={(e) => handleChange('email', e.target.value)}
                placeholder="email@exemplo.com"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Dados da Empresa (Read-only) */}
      <Card className="bg-gray-50">
        <CardHeader>
          <CardTitle className="flex items-center">
            <Building className="h-5 w-5 mr-2" />
            Dados da Empresa
          </CardTitle>
          <CardDescription>Informação gerida pela administração (apenas leitura)</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Secção</Label>
              <Input
                value={perfil?.seccao || 'Não atribuído'}
                disabled
                className="bg-white"
              />
            </div>

            <div className="space-y-2">
              <Label>Função</Label>
              <Input
                value={perfil?.funcao || 'Não atribuído'}
                disabled
                className="bg-white"
              />
            </div>

            <div className="space-y-2">
              <Label>Data de Entrada</Label>
              <Input
                value={perfil?.data_entrada || 'Não definida'}
                disabled
                className="bg-white"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={handleSalvar} disabled={salvando} size="lg">
          <Save className="h-4 w-4 mr-2" />
          {salvando ? 'A guardar...' : 'Guardar Alterações'}
        </Button>
      </div>
    </div>
  );
};

export default MeuPerfilTab;
